import { queryOptions, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { useAllCached, type UseAllCachedOptions } from '../shared/use-all-cached';
import { requireWorkspaceId } from '../shared/workspace';
import { layoutKeys, type Layout, type LayoutListParams } from './entity';
import {
  parseLayoutActionsResponse,
  parseLayoutDetailResponse,
  parseLayoutNewslettersResponse,
  parseLayoutsListResponse,
  parseLayoutStartersResponse,
  parseLayoutTransactionalMessagesResponse,
  populateLayoutActions,
  populateLayoutDetail,
  populateLayoutsList,
  type ParsedLayoutActionsResponse,
  type ParsedLayoutNewslettersResponse,
  type ParsedLayoutsListResponse,
  type ParsedLayoutTransactionalMessagesResponse,
} from './populate';

/**
 * Layout read queries.
 *
 * Layouts follow the same API/DB pattern as snippets: plain async
 * functions perform the wire call and cache population, queryOptions
 * factories define stable keys, and hooks stay thin.
 */

const STALE_TIME = 60_000;

/* ------------------------------------------------------------------------- */
/* getLayouts - active or archived list                                       */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getLayouts(
  workspaceId: number,
  params: LayoutListParams,
  qc: QueryClient,
): Promise<ParsedLayoutsListResponse> {
  const raw = await hydraUiApi.layouts.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: {
        archived: params.archived == null ? undefined : params.archived ? 'true' : 'false',
      },
    },
  });
  const parsed = parseLayoutsListResponse(raw);
  populateLayoutsList(qc, workspaceId, parsed);
  return parsed;
}

export const getLayoutsQueryOptions = (workspaceId: number, params: LayoutListParams, qc: QueryClient) =>
  queryOptions({
    queryKey: layoutKeys.list(workspaceId, params),
    queryFn: () => getLayouts(workspaceId, params, qc),
    staleTime: STALE_TIME,
  });

interface UseLayoutsOptions {
  params?: LayoutListParams;
  queryConfig?: QueryConfig<typeof getLayoutsQueryOptions>;
}

/** @public */
export const useLayouts = ({ params = {}, queryConfig }: UseLayoutsOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getLayoutsQueryOptions(workspaceId, params, qc),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* getLayout - by id                                                          */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getLayout(workspaceId: number, layoutId: string, qc: QueryClient): Promise<Layout> {
  const raw = await hydraUiApi.layouts.get({
    params: { path: { environment_id: String(workspaceId), id: layoutId } },
  });
  const parsed = parseLayoutDetailResponse(raw);
  populateLayoutDetail(qc, workspaceId, parsed);
  return parsed;
}

export const getLayoutQueryOptions = (workspaceId: number, layoutId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: layoutKeys.detail(workspaceId, layoutId),
    queryFn: () => getLayout(workspaceId, layoutId, qc),
    staleTime: STALE_TIME,
  });

interface UseLayoutOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getLayoutQueryOptions>;
}

/** @public */
export const useLayout = ({ id, queryConfig }: UseLayoutOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getLayoutQueryOptions(workspaceId, id, qc),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* getLayoutStarters                                                          */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getLayoutStarters(workspaceId: number) {
  const raw = await hydraUiApi.layouts.getLayoutStarters({
    params: { path: { environment_id: String(workspaceId) } },
  });
  return parseLayoutStartersResponse(raw);
}

export const getLayoutStartersQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: layoutKeys.starters(workspaceId),
    queryFn: () => getLayoutStarters(workspaceId),
    staleTime: STALE_TIME,
  });

interface UseLayoutStartersOptions {
  queryConfig?: QueryConfig<typeof getLayoutStartersQueryOptions>;
}

/** @public */
export const useLayoutStarters = ({ queryConfig }: UseLayoutStartersOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({
    ...getLayoutStartersQueryOptions(workspaceId),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* Layout usage                                                               */
/* ------------------------------------------------------------------------- */

export interface GetLayoutUsageInput {
  workspaceId: number;
  layoutId: string;
}

/** @public */
export async function getLayoutActions(
  input: GetLayoutUsageInput,
  qc: QueryClient,
): Promise<ParsedLayoutActionsResponse> {
  const raw = await hydraUiApi.layouts.getActions({
    params: { path: { environment_id: String(input.workspaceId), id: input.layoutId } },
  });
  const parsed = parseLayoutActionsResponse(raw);
  populateLayoutActions(qc, input.workspaceId, parsed);
  return parsed;
}

export const getLayoutActionsQueryOptions = (input: GetLayoutUsageInput, qc: QueryClient) =>
  queryOptions({
    queryKey: layoutKeys.actionsUsage(input.workspaceId, input.layoutId),
    queryFn: () => getLayoutActions(input, qc),
    staleTime: STALE_TIME,
  });

interface UseLayoutActionsOptions {
  layoutId: string;
  queryConfig?: QueryConfig<typeof getLayoutActionsQueryOptions>;
}

/** @public */
export const useLayoutActions = ({ layoutId, queryConfig }: UseLayoutActionsOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getLayoutActionsQueryOptions({ workspaceId, layoutId }, qc),
    ...queryConfig,
  });
};

/** @public */
export async function getLayoutNewsletters(input: GetLayoutUsageInput): Promise<ParsedLayoutNewslettersResponse> {
  const raw = await hydraUiApi.layouts.getNewsletters({
    params: { path: { environment_id: String(input.workspaceId), id: input.layoutId } },
  });
  return parseLayoutNewslettersResponse(raw);
}

export const getLayoutNewslettersQueryOptions = (input: GetLayoutUsageInput) =>
  queryOptions({
    queryKey: layoutKeys.newslettersUsage(input.workspaceId, input.layoutId),
    queryFn: () => getLayoutNewsletters(input),
    staleTime: STALE_TIME,
  });

interface UseLayoutNewslettersOptions {
  layoutId: string;
  queryConfig?: QueryConfig<typeof getLayoutNewslettersQueryOptions>;
}

/** @public */
export const useLayoutNewsletters = ({ layoutId, queryConfig }: UseLayoutNewslettersOptions) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({
    ...getLayoutNewslettersQueryOptions({ workspaceId, layoutId }),
    ...queryConfig,
  });
};

/** @public */
export async function getLayoutTransactionalMessages(
  input: GetLayoutUsageInput,
): Promise<ParsedLayoutTransactionalMessagesResponse> {
  const raw = await hydraUiApi.layouts.getTransactionalMessages({
    params: { path: { environment_id: String(input.workspaceId), id: input.layoutId } },
  });
  return parseLayoutTransactionalMessagesResponse(raw);
}

export const getLayoutTransactionalMessagesQueryOptions = (input: GetLayoutUsageInput) =>
  queryOptions({
    queryKey: layoutKeys.transactionalMessagesUsage(input.workspaceId, input.layoutId),
    queryFn: () => getLayoutTransactionalMessages(input),
    staleTime: STALE_TIME,
  });

interface UseLayoutTransactionalMessagesOptions {
  layoutId: string;
  queryConfig?: QueryConfig<typeof getLayoutTransactionalMessagesQueryOptions>;
}

/** @public */
export const useLayoutTransactionalMessages = ({ layoutId, queryConfig }: UseLayoutTransactionalMessagesOptions) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({
    ...getLayoutTransactionalMessagesQueryOptions({ workspaceId, layoutId }),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* useLayoutsCached - store-wide read of every cached layout                        */
/* ------------------------------------------------------------------------- */

/** @public */
/** Every cached layout in the workspace. */
export function useLayoutsCached(): readonly Layout[];
/** Every cached layout in the workspace, mapped through `select`. */
export function useLayoutsCached<TSelected>(options: Required<UseAllCachedOptions<Layout, TSelected>>): TSelected;
export function useLayoutsCached<TSelected>(
  options?: UseAllCachedOptions<Layout, TSelected>,
): TSelected | readonly Layout[];
export function useLayoutsCached<TSelected>(
  options?: UseAllCachedOptions<Layout, TSelected>,
): TSelected | readonly Layout[] {
  const workspaceId = requireWorkspaceId();
  return useAllCached<Layout, TSelected>(layoutKeys.details(workspaceId), options);
}
